import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import {
  displayErrorMessage,
  displaySuccess,
} from 'Helpers/FacetNotificationManager';

import { ClientProfileOverride } from 'Resources/Client';

import DataContainer from 'Components/UI/DataContainer';
import ClientCard from './ClientCard';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
}));

export default function PersonalFamilyInformation() {
  const classes = useStyles();
  const { householdResource } = useSimpleHousehold();
  const { collection, setCollection } = useCollection();
  // This clients list is expected to be stale data.
  const [clients, setClients] = useState([]);
  const [activeClientIndex, setActiveClientIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Adds a client to the client-side collection. Does not make a request to the backend.
   */
  const handleAddClient = useCallback(() => {
    const newClient = collection.create({
      metaOverrideSet: ClientProfileOverride,
    });
    setClients(oldClients => ([...oldClients, newClient]));
  }, [collection]);

  useEffect(() => {
    if (householdResource) {
      setIsLoading(true);
      householdResource
        .relationship('clientsWithDependents')
        .link('related')
        .Get({
          metaOverrideSet: ClientProfileOverride,
        })
        .then(coll => setCollection(coll))
        .catch((err) => {
          displayErrorMessage(err, err.errors);
        })
        .then(() => setIsLoading(false));
    }
  }, [householdResource]);

  const handleRemoveClient = async (client, index) => {
    try {
      if (client.link('self')) {
        // TODO fix the delete client endpoint so that it returns non-null data
        await client.delete({ raw: true });
        displaySuccess('Client removed');
        setClients(clients.filter((c, i) => i !== index));
      } else {
        setClients(clients.filter((c, i) => i !== index));
      }
    } catch (err) {
      if (err.errors) {
        displayErrorMessage(err, err.errors);
      } else {
        displayErrorMessage(err, 'An unknown error occurred');
      }
    }
  };

  useEffect(() => {
    if (collection !== null) {
      setClients(
        collection
          .resources()
          .list()
          .sort((a, b) => {
            if (a.attr('isPrimary') === 'true') {
              return -1;
            }
            if (
              b.attr('relationship') !== 'dependent'
              || b.attr('relationship') !== 'child'
            ) {
              return 0;
            }
            return -1;
          }),
      );
    }
  }, [collection]);

  /**
   * Handle onSubmit events. These can either be POST or PUT based on the client state.
   */
  const onSubmit = useCallback(() => {
    setActiveClientIndex(null);
  }, []);

  const onUpdate = useCallback((index) => {
    return () => {
      setActiveClientIndex(index);
    };
  }, []);

  return (
    <DataContainer
      className={classes.root}
      loading={isLoading}
    >
      <div className={classes.root}>
        {clients.map((client, i) => (
          <ClientCard
            key={i} /* eslint-disable-line react/no-array-index-key */
            index={i}
            client={client}
            handleAddClient={handleAddClient}
            handleRemoveClient={c => handleRemoveClient(c, i)}
            clientIndex={i}
            showAddClient={i === clients.length - 1}
            showSaveButton={activeClientIndex === i}
            onUpdate={onUpdate(i)}
            onSubmit={onSubmit}
          />
        ))}
      </div>
    </DataContainer>
  );
}
